/**
 * Small helpers the five public auth pages share (sign in, teacher sign in, forgot password,
 * request access, accept invitation). Page-local: nothing outside src/pages/Login and
 * src/pages/Teachers imports this.
 */

/** International phone format: a plus and 9 to 15 digits. */
export const PHONE_RULE = /^\+[1-9]\d{8,14}$/;

/**
 * The international form of what a person typed. A bare 9-digit number is an Uzbek one.
 * @param {string} raw
 * @returns {string} e.g. "+998901234567"
 */
export function normalizePhone(raw) {
  const kept = String(raw || '').replace(/[^\d+]/g, '');
  if (kept.startsWith('+')) return kept;
  return kept.length === 9 ? `+998${kept}` : `+${kept}`;
}

/**
 * The one sentence a failed auth request shows, in the page language.
 *
 * The API client rejects with { message, code, messageKey? }. `messageKey` marks wording it
 * translated itself (offline, rate limit, server down), so that is safe to show. Whatever the
 * backend said on its own is English, so it never reaches the screen.
 *
 * @param {object} err - what the request rejected with
 * @param {{ badCredentials?: string, fallback: string }} copy - sentences already translated
 * @returns {string}
 */
// The shell sentences that say the request never got a real answer. A "not found" or "check the
// fields" from the shell would be vaguer than the form's own sentence, so those keep the fallback.
const CONNECTION_KEYS = ['network', 'timeout', 'rateLimited', 'serverUnavailable', 'serverError']
  .map((key) => `shell.errors.${key}`);

export function authErrorMessage(err, { badCredentials, fallback }) {
  const code = err?.code ?? err?.response?.status;
  if (code === 401 && badCredentials) return badCredentials;
  if (code === 429 || CONNECTION_KEYS.includes(err?.messageKey)) return err?.message || fallback;
  return fallback;
}

/**
 * Autofocus opens the keyboard on a phone and covers the form, so only a pointer device gets it.
 * @returns {boolean}
 */
export function canAutoFocus() {
  try {
    return !window.matchMedia('(pointer: coarse)').matches;
  } catch {
    return true;
  }
}

/**
 * Splits a translated sentence around one {token} so the page can put a link (or any element)
 * where the token stands without losing the sentence's grammar in each language.
 * @param {string} text - e.g. "Ask an administrator, or write to {email}."
 * @param {string} token - e.g. "{email}"
 * @returns {[string, string]} the text before and after the token (the whole text, then '', if absent)
 */
export function splitAtToken(text, token) {
  const at = String(text).indexOf(token);
  if (at < 0) return [String(text), ''];
  return [text.slice(0, at), text.slice(at + token.length)];
}

/**
 * After a refused submit, put the caret in the first field that needs attention so a keyboard
 * or screen-reader user lands on the message instead of hunting for it. Waits one frame so the
 * error text and the red edge are already in place.
 * @param {string | undefined} id - the input's id (nothing happens without one)
 */
export function focusField(id) {
  if (!id || typeof window === 'undefined') return;
  window.requestAnimationFrame(() => {
    const field = document.getElementById(id);
    if (field) field.focus();
  });
}
